import { useAppSelector } from "../redux/hooks";
import { createSelector } from "@reduxjs/toolkit";
import {
  observationsWithKeyIssues,
  selectKeyIssueMatrixItems,
} from "../utils/keyIssueMatrix";

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

/**
 * Redux state is often synced from multiple sources (e.g. currentReportData
 * vs. a dedicated slice) that can produce a brand-new object/array reference
 * with identical content. Without a content-aware equality check here,
 * `useAppSelector` would return a new reference on every unrelated dispatch,
 * which in turn makes consumers (e.g. form baselines in useDebouncedSave)
 * think the underlying data changed and incorrectly mark forms as dirty.
 * Comparing by content keeps the returned reference stable when nothing
 * actually changed.
 */
function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

const selectCurrentSiteOverview = (state: any) =>
  state?.auditReports?.currentReportData?.siteOverview ||
  state?.auditReports?.currentReportData?.siteOverviewData ||
  state?.auditReports?.currentReportData?.siteData ||
  state?.siteOverviewData?.currentSiteOverview ||
  null;

const selectCurrentWorks = (state: any) =>
  state?.auditReports?.currentReportData?.works ||
  state?.works?.works ||
  EMPTY_ARRAY;

const selectCurrentObservations = (state: any) =>
  state?.auditReports?.currentReportData?.observations ||
  state?.observations?.observations ||
  EMPTY_ARRAY;

const selectObservationsWithKeyIssues = createSelector(
  [selectCurrentObservations, selectKeyIssueMatrixItems],
  observationsWithKeyIssues,
);

const selectCurrentFiles = (state: any) =>
  state?.auditReports?.currentReportData?.files ||
  state?.files?.files ||
  EMPTY_ARRAY;

/**
 * Generic hook for reading the current report data from Redux.
 */
export function useVersionAwareData<T = any>(
  _readOnly: boolean = false,
  liveDataSelector: (state: any) => T = (state) => state,
): T | null {
  return useAppSelector(liveDataSelector, deepEqual);
}

/**
 * Project data for the currently opened report.
 */
export function useVersionAwareProjectData(_readOnly: boolean = false) {
  return useAppSelector(
    (state) => selectCurrentSiteOverview(state)?.projectData || EMPTY_OBJECT,
    deepEqual,
  );
}

/**
 * Site overview data for the currently opened report.
 */
export function useVersionAwareSiteData(_readOnly: boolean = false) {
  return useAppSelector(
    (state) => selectCurrentSiteOverview(state) || EMPTY_OBJECT,
    deepEqual,
  );
}

/**
 * Works/tasks for the currently opened report.
 */
export function useVersionAwareWorks(_readOnly: boolean = false) {
  return useAppSelector(selectCurrentWorks, deepEqual);
}

/**
 * Observations for the currently opened report.
 */
export function useVersionAwareObservations(_readOnly: boolean = false) {
  return useAppSelector(selectCurrentObservations, deepEqual);
}

export function useVersionAwareKeyIssueMatrixItems(_readOnly: boolean = false) {
  return useAppSelector((state) => selectKeyIssueMatrixItems(state), deepEqual);
}

export function useObservationsWithKeyIssues(_readOnly: boolean = false) {
  return useAppSelector(selectObservationsWithKeyIssues, deepEqual);
}

/**
 * Planning aspect data for the currently opened report.
 */
export function useVersionKeyActivitiesPlanned(_readOnly: boolean = false) {
  return useAppSelector(
    (state) =>
      selectCurrentSiteOverview(state)?.planningAspectsData || EMPTY_OBJECT,
    deepEqual,
  );
}

/**
 * Third-party activities/punch list for the currently opened report.
 */
export function useVersionThirdPartyActivites(_readOnly: boolean = false) {
  return useAppSelector(
    (state) => selectCurrentSiteOverview(state)?.punchList || EMPTY_OBJECT,
    deepEqual,
  );
}

/**
 * Files for the currently opened report.
 */
export function useVersionAwareFiles(_readOnly: boolean = false) {
  return useAppSelector(selectCurrentFiles, deepEqual);
}

/**
 * Convenience hook for common current-report data.
 */
export function useCompleteVersionAwareData(_readOnly: boolean = false) {
  const projectData = useVersionAwareProjectData(_readOnly);
  const siteData = useVersionAwareSiteData(_readOnly);
  const works = useVersionAwareWorks(_readOnly);
  const observations = useVersionAwareObservations(_readOnly);
  const files = useVersionAwareFiles(_readOnly);

  return {
    projectData,
    siteData,
    works,
    observations,
    files,
  };
}

// ============================================
// Site Overview Data Hooks (Current Opened Report)
// ============================================

/**
 * Hook to get the complete current site overview data from Redux.
 */
export function useCurrentSiteOverview() {
  return useAppSelector(
    (state) => state?.siteOverviewData?.currentSiteOverview,
    deepEqual,
  );
}

/**
 * Hook to get a specific field from the current site overview data.
 */
export function useSiteOverviewField<T = any>(
  fieldName: string,
): T | undefined {
  return useAppSelector(
    (state) => state?.siteOverviewData?.currentSiteOverview?.[fieldName],
    deepEqual,
  );
}

/**
 * Hook to get project data from the current site overview.
 */
export function useSiteOverviewProjectData() {
  return useAppSelector(
    (state) => state?.siteOverviewData?.currentSiteOverview?.projectData,
    deepEqual,
  );
}

/**
 * Hook to get environmental data from the current site overview.
 */
export function useSiteOverviewEnvironmentalData() {
  return useAppSelector(
    (state) => state?.siteOverviewData?.currentSiteOverview?.environmentalData,
    deepEqual,
  );
}

/**
 * Hook to get accessibility info from the current site overview.
 */
export function useSiteOverviewAccessibilityInfo() {
  return useAppSelector(
    (state) => state?.siteOverviewData?.currentSiteOverview?.accessibilityInfo,
    deepEqual,
  );
}

/**
 * Hook to get facilities from the current site overview.
 */
export function useSiteOverviewFacilities() {
  return useAppSelector(
    (state) => state?.siteOverviewData?.currentSiteOverview?.facilities,
    deepEqual,
  );
}

/**
 * Hook to get safety measures from the current site overview.
 */
export function useSiteOverviewSafetyMeasures() {
  return useAppSelector(
    (state) => state?.siteOverviewData?.currentSiteOverview?.safetyMeasures,
    deepEqual,
  );
}

/**
 * Hook to get site coordinates from the current site overview.
 */
export function useSiteOverviewCoordinates() {
  return useAppSelector(
    (state) => state?.siteOverviewData?.currentSiteOverview?.coordinates,
    deepEqual,
  );
}

/**
 * Hook to get measurements from the current site overview.
 */
export function useSiteOverviewMeasurements() {
  return useAppSelector(
    (state) => state?.siteOverviewData?.currentSiteOverview?.measurements,
    deepEqual,
  );
}

/**
 * Hook to get the basic site overview summary.
 */
export function useSiteOverviewBasicInfo() {
  return useAppSelector((state) => {
    const overview = state?.siteOverviewData?.currentSiteOverview;
    return overview
      ? {
          siteName: overview.siteName,
          location: overview.location,
          siteType: overview.siteType,
          template: overview.template,
        }
      : undefined;
  }, deepEqual);
}

/**
 * Hook to get all aspect data from the current site overview.
 */
export function useSiteOverviewAspectData() {
  return useAppSelector((state) => {
    const overview = state?.siteOverviewData?.currentSiteOverview;
    return overview
      ? {
          siteData: overview.siteData,
          infrastructureData: overview.infrastructureData,
          planningAspectsData: overview.planningAspectsData,
          constructionAspectsData: overview.constructionAspectsData,
          operationalAspectsData: overview.operationalAspectsData,
          operationalData: overview.operationalData,
          gridConnectionData: overview.gridConnectionData,
          landOwnershipData: overview.landOwnershipData,
          shadingObjectsData: overview.shadingObjectsData,
          liftingActivities: overview.liftingActivities,
          punchList: overview.punchList,
        }
      : undefined;
  }, deepEqual);
}

/**
 * Hook to get the loading state of site overview data.
 */
export function useSiteOverviewLoading() {
  return useAppSelector((state) => state?.siteOverviewData?.loading);
}

/**
 * Hook to get the error state of site overview data.
 */
export function useSiteOverviewError() {
  return useAppSelector((state) => state?.siteOverviewData?.error);
}
